<template>
	<div class="home">
		<v-container>
			<v-row>
				<v-col cols="12">
					<v-simple-table>
						<tr>
							<td>
								<v-btn v-clip="currentUser.guid" text>
									Current user
									<v-icon class="pl-2" small>far fa-copy</v-icon>
								</v-btn>
							</td>
							<td class="light-mono">{{ currentUser.guid }}</td>
						</tr>
						<tr>
							<td>
								<v-btn v-clip="cookies.session" text>
									Session
									<v-icon class="pl-2" small>far fa-copy</v-icon>
								</v-btn>
							</td>
							<td class="light-mono">{{ cookies.session }}</td>
						</tr>
						<tr>
							<td>
								<v-btn v-clip="cookies.jwt" text>
									JWT
									<v-icon class="pl-2" small>far fa-copy</v-icon>
								</v-btn>
							</td>
							<td class="light-mono">{{ cookies.jwt }}</td>
						</tr>
					</v-simple-table>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12">
					<h2>Store</h2>
				</v-col>
				<v-col cols="12">
					<v-switch v-model="inMobileApp">
						<template v-slot:label
							>Are we in mobile (native) application?</template
						>
					</v-switch>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12">
					<h2>Roles</h2>
				</v-col>
				<v-col cols="12">Current user roles</v-col>

				<v-col cols="12">
					<v-simple-table>
						<tr v-for="role in currentUserRoles" :key="role">
							<td>
								{{ role }}
							</td>
						</tr>
					</v-simple-table>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12">
					<h2>Permissions</h2>
				</v-col>
				<v-col cols="12">Spoof permissions for testing</v-col>
				<v-col cols="12">
					<v-list>
						<v-list-item-group
							v-model="selectedPermissions"
							multiple
							active-class="primary--text"
						>
							<template v-for="permission in permissions">
								<v-list-item :key="permission" :value="permission">
									<v-list-item-content>
										<v-list-item-title>{{ permission }}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</template>
						</v-list-item-group>
					</v-list>
				</v-col>
			</v-row>

			<v-row>
				<v-col cols="12">
					<h2>API</h2>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="test">API Test</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="getTests">Get Tests</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="report">Install Report</v-btn>
				</v-col>
				<v-col cols="12">
					<h3>Descriptions</h3>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-select
						v-model="group"
						:items="groups"
						label="Select group..."
						clearable
					/>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-select
						v-if="group"
						v-model="description"
						:items="descriptions"
						label="Select device..."
						item-text="name"
						item-value="data"
						clearable
					/>
					<v-card v-if="description" pa-5>
						<v-card-text>{{
							JSON.stringify(description, null, 4)
						}}</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<v-row v-if="inMobileApp">
				<v-col cols="12">
					<h2>Mobile App</h2>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="findController">Find Controller</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="barcode">Bar Code</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="websocket">Websocket Test</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="reprovision">Reprovision</v-btn>
				</v-col>
				<v-col cols="12" class="pa-3">
					<v-btn @click="biometrics">Biometrics</v-btn>
				</v-col>
				<v-col class="pa-5">{{ status }}</v-col>
			</v-row>
		</v-container>
	</div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";

import bridge from "@/api/bridge";
import cloud from "@/api/cloud";

import { supportedPermissions } from "@/services/ability";
import { getAuthToken, getSession } from "@/utils/cookie";

export default {
	components: {},

	data: () => ({
		status: null,
		group: null,
		description: null,

		permissions: supportedPermissions,
	}),

	computed: {
		...mapGetters({
			currentUser: "auth/user",
			currentUserRoles: "auth/roles",
			groups: "descriptions/groups",
			descriptions: "descriptions/filteredByGroup",
			currentPermissions: "auth/permissions",
			hasRole: "auth/hasRole",
		}),

		isAdmin() {
			return this.hasRole("Admin");
		},

		inMobileApp: {
			get() {
				return this.$store.getters["config/inMobileApp"];
			},
			set(value) {
				this.setInMobileApp(value);
			},
		},

		selectedPermissions: {
			get() {
				return this.$store.state.auth.testPermissions;
			},
			set(value) {
				this.$store.commit("auth/setTestPermissions", value);
			},
		},

		cookies() {
			const session = getSession();
			const jwt = getAuthToken();
			return { session, jwt };
		},
	},

	watch: {
		group(value) {
			this.description = null;
			if (value) {
				this.$store.dispatch("descriptions/filter", value);
			}
		},
	},

	async created() {
		await this.getData();
		await this.test();
	},

	methods: {
		...mapMutations("config", ["setInMobileApp"]),

		async findController() {
			let controller = "e076d05c8523";
			this.status = `Finding controller ${controller}`;
			let result = await bridge.findController(
				controller,
				"209ef92b-382b-4357-b891-cff042362a46"
			);
			this.status = JSON.stringify(result, null, 4);
		},

		async barcode() {
			let result = await bridge.barcode();
			this.status = `Barcode read: ${result}`;
		},

		async websocket() {
			this.status = "";

			if (this.socket) {
				this.status += ` Closing old websocket...`;
				this.socket.close();
				this.socket = null;
			}

			this.status += ` Opening websocket...`;

			/* global io */
			this.socket = io("https://10.10.10.111:8000", {
				transports: ["websocket"],
				forceNew: true,
			});

			this.socket.on("connect", () => {
				this.status += ` Connected.`;
			});

			this.socket.on("connect_error", e => {
				this.status += ` ${e}`;
			});

			this.socket.on("error", e => {
				if (e === "invalid namespace") {
					this.status += ` Connected.`;
					this.socket.close();
					this.socket = null;
				} else {
					this.status += ` ${e}`;
				}
			});
		},

		async reprovision() {
			this.status = `Reprovisioning...`;

			await bridge.reprovisionController("10.10.10.111", "e076d05c8523");
			this.status = `Reprovisioned`;
		},

		async getData() {
			await this.$store.dispatch("descriptions/load");
		},

		async test() {
			let { community, system } = await cloud.getBySerial("e076d05c8523");
			console.log(community, system);
		},

		async report() {
			await cloud.submitReport("209ef92b-382b-4357-b891-cff042362a46", {
				test: "hello",
			});
		},

		async getTests() {
			await this.$store.dispatch(
				"controller/loadLocal",
				"209ef92b-382b-4357-b891-cff042362a46"
			);

			let device = {
				cls: "com.k4connect.zwave.dimmer",
				name: "Dimmer",
				path: "Devices/Guest Room/Dimmer",
			};
			/*
			let device = {
				cls: 'com.gocontrol.tbz48',
				name: 'Thermostat',
				path: 'Devices/Guest Room/Thermostat'
			};
			*/

			let tests = await this.$store.dispatch("wizard/getTests", device);
			await tests[0].run();
		},

		async biometrics() {
			let isAvailable = await bridge.biometricIsAvailable();

			if (isAvailable) {
				await bridge.biometricStore("testuser", "testpass");

				let results;

				try {
					results = await bridge.biometricLogin();
				} catch (e) {
					this.status = e;
				}

				this.status = JSON.stringify(results);
			} else {
				this.status = "Biometrics not available";
			}
		},
	},
};
</script>

<style>
.mobile-tile-title {
	font-size: 24px;
}
.tile-title {
	word-break: normal;
	text-align: center;
}
</style>
